import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Icon, Text } from '@/lib/polaris';
import {
  XSmallIcon, ImportIcon, ChevronLeftIcon, ChevronRightIcon,
  ChatIcon, MinusCircleIcon, PlusCircleIcon,
  StarIcon, StarFilledIcon, PinIcon, PinFilledIcon,
  UndoIcon, ShareIcon, SmileyHappyIcon,
} from '@/lib/polaris-icons';
import type { WaMessage, MessageReaction } from './WaMessageThread';

// ─── WaImageLightbox ───

const QUICK_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🙏'];

interface LightboxProps {
  /** Mensajes a navegar en el carrusel. Cada uno aporta media_url, thumbnail y metadata. */
  msgs: WaMessage[];
  /** Index inicial dentro de `msgs`. Default 0. */
  initialIndex?: number;
  /** Avatar del contacto (para mensajes inbound). */
  contactPhotoUrl?: string | null;
  /** Avatar del agente actual (para mensajes outbound). */
  currentUserAvatarUrl?: string | null;
  /** ID del mensaje fijado en la conversación (si lo hay). */
  pinnedMessageId?: string | null;
  onClose: () => void;
  /** Toggle de "destacar" mensaje. */
  onStarMessage?: (msgId: string) => void;
  /** Solicitar fijar el mensaje. El caller debe abrir el modal de
   *  selección de duración (24h/7d/30d) — patrón existente del thread. */
  onRequestPin?: (msg: WaMessage) => void;
  /** Desfijar el mensaje fijado actual. */
  onUnpinMessage?: () => void;
  /** Responder al mensaje (cierra el lightbox y enfoca el composer). */
  onReplyMessage?: (msg: WaMessage) => void;
  /** Reenviar mensaje (cierra el lightbox y abre selector de destinos). */
  onForwardMessage?: (msg: WaMessage) => void;
  /** Enviar reacción al mensaje. */
  onSendReaction?: (msgId: string, emoji: string) => void;
  /** Cerrar lightbox y hacer scroll al mensaje en el thread. */
  onGoToMessage?: (msgId: string) => void;
}

const ZOOM_MIN = 1;
const ZOOM_MAX = 5;
const ZOOM_STEP = 0.2;

function formatLightboxTimestamp(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
  const isYesterday = d.toDateString() === yesterday.toDateString();
  const time = d.toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' });
  if (sameDay) return `Hoy a la(s) ${time}`;
  if (isYesterday) return `Ayer a la(s) ${time}`;
  return d.toLocaleDateString('es-PE', { day: 'numeric', month: 'short', year: 'numeric' }) + ` ${time}`;
}

export function WaImageLightbox({
  msgs, initialIndex = 0,
  contactPhotoUrl, currentUserAvatarUrl, pinnedMessageId,
  onClose,
  onStarMessage, onRequestPin, onUnpinMessage,
  onReplyMessage, onForwardMessage, onSendReaction, onGoToMessage,
}: LightboxProps) {
  // Items derivados (URL + thumb) — recalcular si los msgs cambian (p.ej.
  // un download asincrónico actualiza media_url de un thumbnail).
  const items = useMemo(() => msgs
    .map((m) => ({ url: m.media_url ?? m.media_thumbnail ?? '', thumbUrl: m.media_thumbnail ?? null }))
    .filter((it) => it.url),
    [msgs]);

  const [idx, setIdx] = useState(Math.max(0, Math.min(initialIndex, items.length - 1)));
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [rotation, setRotation] = useState(0);
  const [reactionPickerOpen, setReactionPickerOpen] = useState(false);
  const dragRef = useRef<{ startX: number; startY: number; baseX: number; baseY: number; moved: boolean } | null>(null);
  // Ref del div CENTRAL donde vive la imagen (no del modal entero).
  // Necesario para que el cálculo de zoom-on-cursor use el centro REAL
  // de la imagen — el centro del modal completo está más arriba que el
  // centro de la imagen porque hay header + counter + strip que ocupan
  // espacio en el viewport.
  const containerRef = useRef<HTMLDivElement | null>(null);
  const thumbStripRef = useRef<HTMLDivElement | null>(null);
  // Flag para evitar la animación al abrir el lightbox: el strip aparece
  // ya posicionado en el thumb activo, sin scroll animado desde el inicio.
  const firstScrollRef = useRef(true);

  const current = items[idx];
  const currentMsg = msgs[idx];
  const hasNav = items.length > 1;
  const isOutbound = currentMsg?.direction === 'outbound';
  const isStarred = (currentMsg as WaMessage & { is_starred?: boolean } | undefined)?.is_starred ?? false;
  const isPinned = !!currentMsg && pinnedMessageId === currentMsg.id;
  const senderName = isOutbound ? 'Tú' : undefined;
  const senderAvatarUrl = isOutbound ? currentUserAvatarUrl : contactPhotoUrl;
  const createdAt = currentMsg?.created_at;

  // Reset zoom/pan/rotación + cerrar picker al cambiar de imagen.
  const resetView = useCallback(() => {
    setZoom(1); setPan({ x: 0, y: 0 }); setRotation(0);
    setReactionPickerOpen(false);
  }, []);

  // Handlers de las acciones del header. Usan `currentMsg` para apuntar
  // al mensaje activo (el que se está viendo).
  const handleStar = useCallback(() => {
    if (!currentMsg || !onStarMessage) return;
    onStarMessage(currentMsg.id);
  }, [currentMsg, onStarMessage]);

  const handlePin = useCallback(() => {
    if (!currentMsg) return;
    if (isPinned) onUnpinMessage?.();
    else onRequestPin?.(currentMsg);
  }, [currentMsg, isPinned, onRequestPin, onUnpinMessage]);

  const handleReply = useCallback(() => {
    if (!currentMsg) return;
    onReplyMessage?.(currentMsg);
  }, [currentMsg, onReplyMessage]);

  const handleForward = useCallback(() => {
    if (!currentMsg) return;
    onForwardMessage?.(currentMsg);
  }, [currentMsg, onForwardMessage]);

  const handleReaction = useCallback((emoji: string) => {
    if (!currentMsg || !onSendReaction) return;
    onSendReaction(currentMsg.id, emoji);
    setReactionPickerOpen(false);
  }, [currentMsg, onSendReaction]);

  // No-wrap: en el primer / último item, las flechas no hacen nada.
  // El usuario es responsable visualmente del estado disabled.
  const goPrev = useCallback(() => {
    if (!hasNav || idx <= 0) return;
    setIdx(idx - 1);
    resetView();
  }, [hasNav, idx, resetView]);

  const goNext = useCallback(() => {
    if (!hasNav || idx >= items.length - 1) return;
    setIdx(idx + 1);
    resetView();
  }, [hasNav, idx, items.length, resetView]);

  const atFirst = idx <= 0;
  const atLast = idx >= items.length - 1;

  // Wheel zoom centrado en la imagen (estilo WhatsApp Web).
  // No usamos zoom-on-cursor porque, aunque matemáticamente mantiene
  // el píxel bajo el cursor estable, perceptualmente parece que la
  // imagen "se va al lado opuesto" al hacer zoom — confunde al usuario.
  // El pan se mantiene proporcional al zoom para que el punto central
  // de la imagen permanezca centrado.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const delta = e.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP;
      setZoom((prev) => {
        const next = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, prev + delta * Math.max(1, prev)));
        if (next === ZOOM_MIN) {
          setPan({ x: 0, y: 0 });
        } else if (prev > 0) {
          // Mantener el centro visual estable: escalar el pan junto con
          // el zoom para que el punto que estaba en el centro de la
          // imagen siga ahí tras el cambio.
          const ratio = next / prev;
          setPan((p) => ({ x: p.x * ratio, y: p.y * ratio }));
        }
        return next;
      });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  // Teclado: ESC cierra, flechas navegan.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, goPrev, goNext]);

  // Auto-scroll del thumb strip para mantener visible la imagen actual.
  // Primer scroll = instant (al abrir, posicionado ya en el medio).
  // Siguientes = smooth (transición elegante al navegar prev/next).
  useEffect(() => {
    const strip = thumbStripRef.current;
    if (!strip) return;
    const active = strip.querySelector<HTMLElement>(`[data-thumb-idx="${idx}"]`);
    if (!active) return;
    active.scrollIntoView({
      behavior: firstScrollRef.current ? 'auto' : 'smooth',
      inline: 'center',
      block: 'nearest',
    });
    firstScrollRef.current = false;
  }, [idx]);

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    if (zoom <= 1) return;
    dragRef.current = { startX: e.clientX, startY: e.clientY, baseX: pan.x, baseY: pan.y, moved: false };
  }, [zoom, pan]);

  const onMouseMove = useCallback((e: React.MouseEvent) => {
    const d = dragRef.current;
    if (!d) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) d.moved = true;
    setPan({ x: d.baseX + dx, y: d.baseY + dy });
  }, []);

  const onMouseUp = useCallback(() => {
    dragRef.current = null;
  }, []);

  const onBgClick = useCallback(() => {
    // Solo cierra si NO hay zoom ni drag pendiente.
    if (zoom > 1) return;
    onClose();
  }, [zoom, onClose]);

  const onDoubleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (zoom > 1) { setZoom(1); setPan({ x: 0, y: 0 }); }
    else setZoom(2);
  }, [zoom]);

  const zoomIn = useCallback(() => {
    setZoom((z) => Math.min(ZOOM_MAX, z + 0.5));
  }, []);

  const zoomOut = useCallback(() => {
    setZoom((z) => {
      const next = Math.max(ZOOM_MIN, z - 0.5);
      if (next === ZOOM_MIN) setPan({ x: 0, y: 0 });
      return next;
    });
  }, []);

  const handleGoTo = useCallback(() => {
    if (!currentMsg || !onGoToMessage) return;
    onGoToMessage(currentMsg.id);
  }, [currentMsg, onGoToMessage]);

  if (!current) return null;
  // Portal a document.body. Sin esto, si algún ancestor del thread tiene
  // `transform`, `filter`, `will-change` o `contain`, crea un containing
  // block y `position: fixed` queda restringido a su rect — el modal
  // termina recortado debajo de la navbar/sidebar de la app.
  if (typeof document === 'undefined') return null;

  const headerBtn: React.CSSProperties = {
    width: 36, height: 36, borderRadius: '50%',
    background: 'transparent', border: 'none',
    color: '#54656f', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: 0,
    flexShrink: 0,
  };
  // Wrapper para los iconos: Polaris Icon hereda color del parent.
  // Forzamos color explícito (#54656f, gris WhatsApp) sobre fondo claro.
  const iconWrap: React.CSSProperties = {
    width: 22, height: 22, display: 'flex',
    alignItems: 'center', justifyContent: 'center',
    color: '#54656f',
  };

  return createPortal(
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 99999,
        background: '#f0f2f5',
        display: 'flex', flexDirection: 'column',
        userSelect: 'none',
      }}
    >
      {/* Forzar color visible en los iconos del header. Sin esto, Polaris
          resuelve `var(--p-color-icon)` que sobre fondo claro queda
          casi imperceptible. */}
      <style>{`
        .wa-lightbox-action { color: #54656f; }
        .wa-lightbox-action:hover { background: rgba(11,20,26,0.05) !important; }
        .wa-lightbox-action svg { color: inherit !important; fill: currentColor !important; }
        .wa-lightbox-action .Polaris-Icon { color: inherit !important; }
      `}</style>
      {/* ─── Header ─── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '12px 16px',
        background: '#f0f2f5',
        borderBottom: '1px solid #d1d7db',
        flexShrink: 0,
      }}>
        {/* Sender info (left) */}
        <div style={{
          width: 40, height: 40, borderRadius: '50%',
          background: '#dfe5e7', overflow: 'hidden',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          {senderAvatarUrl ? (
            <img src={senderAvatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <span style={{ fontSize: 16, color: '#54656f', fontWeight: 500 }}>
              {(senderName ?? '?').charAt(0).toUpperCase()}
            </span>
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Text as="p" variant="bodyMd" fontWeight="semibold" truncate>
            {senderName ?? 'Mensaje'}
          </Text>
          {createdAt ? (
            <Text as="p" variant="bodySm" tone="subdued">
              {formatLightboxTimestamp(createdAt)}
            </Text>
          ) : null}
        </div>

        {/* Action icons (right). Orden estilo WhatsApp Web:
            Alejar | Acercar | Ir al mensaje | Responder | Destacar | Fijar |
            Reaccionar | Reenviar | Descargar | Cerrar
            Todos con `title` nativo para tooltip al hover. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
          <button
            type="button" className="wa-lightbox-action" style={headerBtn}
            title="Alejar" aria-label="Alejar"
            onClick={zoomOut}
            disabled={zoom <= ZOOM_MIN}
          >
            <span style={iconWrap}><Icon source={MinusCircleIcon} /></span>
          </button>
          <button
            type="button" className="wa-lightbox-action" style={headerBtn}
            title="Acercar" aria-label="Acercar"
            onClick={zoomIn}
            disabled={zoom >= ZOOM_MAX}
          >
            <span style={iconWrap}><Icon source={PlusCircleIcon} /></span>
          </button>
          <button
            type="button" className="wa-lightbox-action" style={headerBtn}
            title="Ir al mensaje" aria-label="Ir al mensaje"
            onClick={handleGoTo}
          >
            <span style={iconWrap}><Icon source={ChatIcon} /></span>
          </button>
          <button
            type="button" className="wa-lightbox-action" style={headerBtn}
            title="Responder" aria-label="Responder"
            onClick={handleReply}
          >
            <span style={iconWrap}><Icon source={UndoIcon} /></span>
          </button>
          <button
            type="button"
            className="wa-lightbox-action"
            style={{ ...headerBtn, color: isStarred ? '#f5b300' : '#54656f' }}
            title={isStarred ? 'Quitar destacado' : 'Destacar'}
            aria-label={isStarred ? 'Quitar destacado' : 'Destacar'}
            onClick={handleStar}
          >
            <span style={iconWrap}>
              <Icon source={isStarred ? StarFilledIcon : StarIcon} />
            </span>
          </button>
          <button
            type="button"
            className="wa-lightbox-action"
            style={{ ...headerBtn, color: isPinned ? '#00a884' : '#54656f' }}
            title={isPinned ? 'Desfijar' : 'Fijar'}
            aria-label={isPinned ? 'Desfijar' : 'Fijar'}
            onClick={handlePin}
          >
            <span style={iconWrap}><Icon source={isPinned ? PinFilledIcon : PinIcon} /></span>
          </button>
          {/* Reaction picker: dropdown con 6 emojis comunes. */}
          <div style={{ position: 'relative' }}>
            <button
              type="button"
              className="wa-lightbox-action"
              style={headerBtn}
              title="Reaccionar" aria-label="Reaccionar"
              onClick={() => setReactionPickerOpen((v) => !v)}
            >
              <span style={iconWrap}><Icon source={SmileyHappyIcon} /></span>
            </button>
            {reactionPickerOpen ? (
              <>
                <div
                  style={{ position: 'fixed', inset: 0, zIndex: 1 }}
                  onClick={() => setReactionPickerOpen(false)}
                />
                <div style={{
                  position: 'absolute', top: 'calc(100% + 6px)', right: 0,
                  background: '#fff', borderRadius: 28,
                  padding: '8px 10px',
                  boxShadow: '0 4px 16px rgba(11,20,26,0.18)',
                  display: 'flex', gap: 4,
                  zIndex: 2,
                }}>
                  {QUICK_REACTIONS.map((emoji) => (
                    <button
                      key={emoji}
                      type="button"
                      onClick={() => handleReaction(emoji)}
                      style={{
                        width: 36, height: 36, borderRadius: '50%',
                        border: 'none', background: 'transparent',
                        cursor: 'pointer', fontSize: 22,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        padding: 0,
                      }}
                      aria-label={`Reaccionar con ${emoji}`}
                      title={`Reaccionar con ${emoji}`}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              </>
            ) : null}
          </div>
          <button
            type="button" className="wa-lightbox-action" style={headerBtn}
            title="Reenviar" aria-label="Reenviar"
            onClick={handleForward}
          >
            <span style={iconWrap}><Icon source={ShareIcon} /></span>
          </button>
          <a
            href={current.url}
            download
            target="_blank"
            rel="noopener noreferrer"
            className="wa-lightbox-action"
            style={{ ...headerBtn, textDecoration: 'none' }}
            title="Descargar" aria-label="Descargar"
            onClick={(e) => e.stopPropagation()}
          >
            <span style={iconWrap}><Icon source={ImportIcon} /></span>
          </a>
          <button
            type="button" className="wa-lightbox-action" style={headerBtn}
            title="Cerrar" aria-label="Cerrar"
            onClick={onClose}
          >
            <span style={iconWrap}><Icon source={XSmallIcon} /></span>
          </button>
        </div>
      </div>

      {/* ─── Body: imagen central + flechas ─── */}
      <div
        ref={containerRef}
        style={{
          flex: 1, position: 'relative',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          overflow: 'hidden',
          padding: '24px 80px',
          boxSizing: 'border-box',
          cursor: zoom > 1 ? (dragRef.current ? 'grabbing' : 'grab') : 'default',
        }}
        onClick={onBgClick}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
      >
        {hasNav ? (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); goPrev(); }}
            disabled={atFirst}
            style={{
              position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)',
              width: 48, height: 48, borderRadius: '50%',
              background: atFirst ? 'rgba(0,0,0,0.25)' : 'rgba(0,0,0,0.55)',
              border: 'none',
              color: '#fff',
              cursor: atFirst ? 'not-allowed' : 'pointer',
              opacity: atFirst ? 0.5 : 1,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 5,
              transition: 'opacity 120ms, background 120ms',
            }}
            aria-label="Anterior"
          >
            <span style={{ color: '#fff' }}><Icon source={ChevronLeftIcon} /></span>
          </button>
        ) : null}

        <img
          src={current.url}
          alt=""
          draggable={false}
          onClick={(e) => e.stopPropagation()}
          onDoubleClick={onDoubleClick}
          style={{
            maxWidth: '100%', maxHeight: '100%',
            width: 'auto', height: 'auto',
            objectFit: 'contain',
            borderRadius: 4,
            boxShadow: '0 2px 16px rgba(0,0,0,0.15)',
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom}) rotate(${rotation}deg)`,
            transformOrigin: 'center center',
            transition: dragRef.current ? 'none' : 'transform 100ms ease-out',
            cursor: zoom > 1 ? (dragRef.current ? 'grabbing' : 'grab') : 'default',
            willChange: 'transform',
            background: '#fff',
          }}
        />

        {hasNav ? (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); goNext(); }}
            disabled={atLast}
            style={{
              position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)',
              width: 48, height: 48, borderRadius: '50%',
              background: atLast ? 'rgba(0,0,0,0.25)' : 'rgba(0,0,0,0.55)',
              border: 'none',
              color: '#fff',
              cursor: atLast ? 'not-allowed' : 'pointer',
              opacity: atLast ? 0.5 : 1,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 5,
              transition: 'opacity 120ms, background 120ms',
            }}
            aria-label="Siguiente"
          >
            <span style={{ color: '#fff' }}><Icon source={ChevronRightIcon} /></span>
          </button>
        ) : null}
      </div>

      {/* ─── Counter "X de N" ─── */}
      {hasNav ? (
        <div style={{
          textAlign: 'center', padding: '4px 0 8px',
          fontSize: 13, color: '#54656f',
          flexShrink: 0,
        }}>
          {idx + 1} de {items.length}
        </div>
      ) : null}

      {/* ─── Strip de thumbnails ─── */}
      {/* Outer hace scroll. Inner es flex con `width: 100%` + `minWidth:
          min-content` + `justifyContent: center`:
          - Si los thumbs CABEN → inner=100% del outer, justify-center
            centra el grupo (sin scroll).
          - Si NO caben → minWidth fuerza al inner a expandirse al ancho
            del contenido, justify-center no aplica (no hay espacio extra),
            y el outer scrollea. scrollIntoView posiciona el activo:
            centrado en el medio, contra el borde en los extremos. */}
      {hasNav ? (
        <div
          ref={thumbStripRef}
          style={{
            background: '#f0f2f5',
            borderTop: '1px solid #d1d7db',
            overflowX: 'auto',
            scrollbarWidth: 'thin',
            flexShrink: 0,
          }}
        >
          <div style={{
            display: 'flex', gap: 6,
            padding: '12px 24px',
            width: '100%',
            minWidth: 'min-content',
            justifyContent: 'center',
            boxSizing: 'border-box',
          }}>
            {items.map((it, i) => {
              const active = i === idx;
              const src = it.thumbUrl || it.url;
              return (
                <button
                  key={i}
                  type="button"
                  data-thumb-idx={i}
                  onClick={() => { setIdx(i); resetView(); }}
                  style={{
                    width: 60, height: 80, flexShrink: 0,
                    border: active ? '3px solid #00a884' : '3px solid transparent',
                    borderRadius: 4,
                    background: '#dfe5e7',
                    cursor: 'pointer',
                    padding: 0, overflow: 'hidden',
                    opacity: active ? 1 : 0.6,
                    transition: 'opacity 120ms, border-color 120ms',
                  }}
                  aria-label={`Imagen ${i + 1}`}
                >
                  <img
                    src={src}
                    alt=""
                    style={{
                      width: '100%', height: '100%', objectFit: 'cover',
                      display: 'block',
                    }}
                  />
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>,
    document.body,
  );
}

// ─── WaPinModal ───

interface PinModalProps {
  msg: WaMessage;
  onPin: (msg: WaMessage, duration: string) => void;
  onClose: () => void;
}

export function WaPinModal({ msg, onPin, onClose }: PinModalProps) {
  const [pinDuration, setPinDuration] = useState('7d');
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9998,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div
        style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)' }}
        onClick={onClose}
      />
      <div style={{
        position: 'relative', width: 420, background: '#fff',
        borderRadius: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
        padding: '24px 28px',
      }}>
        <h3 style={{ margin: '0 0 8px', fontSize: 17, fontWeight: 600, color: '#111b21' }}>
          Selecciona por cuánto tiempo quieres fijar el mensaje
        </h3>
        <p style={{ margin: '0 0 20px', fontSize: 14, color: '#667781' }}>
          Puedes desfijarlo en cualquier momento.
        </p>
        {(['24h', '7d', '30d'] as const).map((val) => {
          const labels: Record<string, string> = { '24h': '24 horas', '7d': '7 días', '30d': '30 días' };
          return (
            <label
              key={val}
              style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '12px 4px', cursor: 'pointer',
                borderBottom: val !== '30d' ? '1px solid #f0f2f5' : 'none',
              }}
            >
              <div style={{
                width: 20, height: 20, borderRadius: '50%',
                border: pinDuration === val ? '6px solid #008069' : '2px solid #8696a0',
                boxSizing: 'border-box',
                transition: 'border 0.15s',
              }} />
              <input
                type="radio"
                name="pin-duration"
                value={val}
                checked={pinDuration === val}
                onChange={() => setPinDuration(val)}
                style={{ display: 'none' }}
              />
              <span style={{ fontSize: 15, color: '#111b21' }}>{labels[val]}</span>
            </label>
          );
        })}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 24 }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              border: 'none', background: 'transparent', cursor: 'pointer',
              fontSize: 14, fontWeight: 600, color: '#008069',
              padding: '10px 20px', borderRadius: 20,
            }}
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => { onPin(msg, pinDuration); onClose(); }}
            style={{
              border: 'none', background: '#008069', color: '#fff',
              cursor: 'pointer', fontSize: 14, fontWeight: 600,
              padding: '10px 24px', borderRadius: 20,
            }}
          >
            Fijar
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── WaReactionDetailModal ───

interface ReactionDetailModalProps {
  messages: WaMessage[];
  msgId: string;
  onRemoveOwnReaction: () => void;
  onClose: () => void;
}

export function WaReactionDetailModal({ messages, msgId, onRemoveOwnReaction, onClose }: ReactionDetailModalProps) {
  const [tab, setTab] = useState('all');
  const detailMsg = messages.find((m) => m.id === msgId);
  if (!detailMsg || !detailMsg.reactions?.length) return null;

  const grouped = new Map<string, MessageReaction[]>();
  for (const r of detailMsg.reactions) {
    const list = grouped.get(r.emoji) ?? [];
    list.push(r);
    grouped.set(r.emoji, list);
  }
  const detailTabs = [
    { key: 'all', label: `Todas ${detailMsg.reactions.length}` },
    ...Array.from(grouped.entries()).map(([emoji, list]) => ({ key: emoji, label: `${emoji} ${list.length}` })),
  ];
  const visibleReactions = tab === 'all' ? detailMsg.reactions : (grouped.get(tab) ?? []);

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={onClose}
    >
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.3)' }} />
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'relative',
          width: '90%', maxWidth: 360, maxHeight: '60vh',
          background: 'var(--p-color-bg-surface)',
          borderRadius: 16,
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px 8px' }}>
          <Text as="span" variant="headingSm">Reacciones</Text>
          <button
            type="button"
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--p-color-text-subdued)', padding: 4, display: 'flex' }}
          >
            <Icon source={XSmallIcon} tone="subdued" />
          </button>
        </div>
        <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--p-color-border-subdued)', padding: '0 16px', overflowX: 'auto' }}>
          {detailTabs.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              style={{
                padding: '8px 14px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 14,
                fontWeight: tab === t.key ? 600 : 400,
                color: tab === t.key ? 'var(--p-color-text-brand)' : 'var(--p-color-text-subdued)',
                borderBottom: tab === t.key ? '2px solid var(--p-color-text-brand)' : '2px solid transparent',
                whiteSpace: 'nowrap', transition: 'all 0.15s',
              }}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
          {visibleReactions.map((r, i) => {
            const isOwn = r.from_me || r.from === 'agent';
            return (
              <div
                key={`${r.from}-${i}`}
                role={isOwn ? 'button' : undefined}
                tabIndex={isOwn ? 0 : undefined}
                onClick={isOwn ? () => { onRemoveOwnReaction(); onClose(); } : undefined}
                style={{
                  display: 'flex', alignItems: 'center', padding: '10px 16px', gap: 12,
                  cursor: isOwn ? 'pointer' : 'default',
                  transition: 'background 0.15s',
                }}
                onMouseEnter={isOwn ? (e) => { e.currentTarget.style.background = 'var(--p-color-bg-surface-hover)'; } : undefined}
                onMouseLeave={isOwn ? (e) => { e.currentTarget.style.background = ''; } : undefined}
              >
                <div style={{
                  width: 40, height: 40, borderRadius: '50%',
                  background: isOwn ? '#DCF8C6' : 'var(--p-color-bg-surface-secondary)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 18, flexShrink: 0,
                }}>
                  {isOwn ? '🧑‍💼' : '👤'}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text as="p" variant="bodySm" fontWeight="semibold">
                    {isOwn ? 'Tú' : r.from}
                  </Text>
                  {isOwn ? (
                    <Text as="p" variant="bodySm" tone="subdued">Toca para eliminar</Text>
                  ) : null}
                </div>
                <span style={{ fontSize: 24 }}>{r.emoji}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
